import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal, Alert } from 'react-bootstrap';
import Adminlayout from "../../../../layouts/admin/Adminlayout";
import { Pie, Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Download, Calendar, TrendingUp, Users, DollarSign, FileText, Award, Mail, RefreshCw, CheckCircle } from 'lucide-react';
import '../../../../assets/css/admin/dashboardCSS/Report.css';
import TitleBar from '../../../../subscreens/admin/TitleBar';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Reports = () => {
  // State Management
  const [reportType, setReportType] = useState('Monthly Report');
  const [selectedMonth, setSelectedMonth] = useState('January 2026');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSubject, setEmailSubject] = useState('Monthly Donation Report - January 2026');
  const [emailMessage, setEmailMessage] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  // Report Types
  const reportTypes = [
    'Monthly Report',
    'Quarterly Report',
    'Annual Report',
    'Custom Date Range'
  ];

  // Donations by Category Data
  const donationsByCategoryData = {
    labels: ['Fees', 'Books', 'Uniforms', 'Other Expenses'],
    datasets: [
      {
        label: 'Amount (PKR)',
        data: [450000, 180000, 95000, 125000],
        backgroundColor: [
          '#10183f',
          '#28a745',
          '#ffc107',
          '#dc3545'
        ],
        borderColor: '#ffffff',
        borderWidth: 2
      }
    ]
  };

  // Monthly Donations Trend
  const monthlyTrendData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Donations (PKR)',
        data: [350000, 420000, 380000, 450000, 490000, 520000, 480000, 550000, 580000, 610000, 590000, 640000],
        borderColor: '#10183f',
        backgroundColor: 'rgba(16, 24, 63, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#10183f',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 5
      }
    ]
  };

  // Requests Status Distribution
  const requestsStatusData = {
    labels: ['Approved', 'Pending', 'Rejected', 'Completed'],
    datasets: [
      {
        data: [145, 38, 22, 95],
        backgroundColor: ['#28a745', '#ffc107', '#dc3545', '#10183f'],
        borderColor: '#ffffff',
        borderWidth: 2
      }
    ]
  };

  // Students by Institute
  const studentsByInstituteData = {
    labels: ['ABC University', 'XYZ College', 'PQR Institute', 'LMN University', 'Others'],
    datasets: [
      {
        label: 'Number of Students',
        data: [85, 62, 48, 55, 35],
        backgroundColor: '#10183f',
        borderRadius: 8
      }
    ]
  };

  // Students Helped Summary
  const studentsHelpedData = [
    { id: 1, name: 'Ali Ahmed', institute: 'ABC University', totalReceived: 45000, requestsApproved: 3, lastRequest: '2024-01-15' },
    { id: 2, name: 'Sara Khan', institute: 'XYZ College', totalReceived: 28000, requestsApproved: 2, lastRequest: '2024-01-10' },
    { id: 3, name: 'Ahmed Raza', institute: 'PQR Institute', totalReceived: 52000, requestsApproved: 4, lastRequest: '2024-01-20' },
    { id: 4, name: 'Fatima Ali', institute: 'ABC University', totalReceived: 38000, requestsApproved: 3, lastRequest: '2024-01-05' },
    { id: 5, name: 'Hassan Malik', institute: 'LMN University', totalReceived: 31000, requestsApproved: 2, lastRequest: '2024-01-25' }
  ];

  // Key Metrics
  const keyMetrics = {
    totalDonations: 1850000,
    totalStudents: 245,
    requestsProcessed: 300,
    successRate: 82,
    averageDonation: 7551,
    topCategory: 'Fees'
  };

  // Handle Download PDF
  const handleDownloadPDF = () => {
    console.log('Downloading PDF report...');
    alert('PDF report download started!');
  };

  // Handle Email Report
  const handleEmailReport = () => {
    setShowEmailModal(true);
  };

  const sendEmailReport = () => {
    if (!emailAddress.trim()) {
      alert('Please enter an email address!');
      return;
    }

    console.log('Sending email report to:', emailAddress);
    console.log('Subject:', emailSubject);
    console.log('Message:', emailMessage);

    setShowEmailModal(false);
    setShowSuccessAlert(true);
    setTimeout(() => setShowSuccessAlert(false), 5000);

    setEmailAddress('');
    setEmailMessage('');
  };

  // Handle Manual Refresh
  const handleManualRefresh = () => {
    setIsRefreshing(true);
    console.log('Manually refreshing data...');
    
    setTimeout(() => {
      setIsRefreshing(false);
      setLastUpdated(new Date());
      alert('Data refreshed successfully!');
    }, 1500);
  };

  // Auto-refresh Effect
  useEffect(() => {
    let intervalId;

    if (autoRefresh) {
      intervalId = setInterval(() => {
        console.log('Auto-refreshing data...');
        setLastUpdated(new Date());
      }, 300000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh]);

  // Format last updated time
  const formatLastUpdated = () => {
    const now = new Date();
    const diff = Math.floor((now - lastUpdated) / 1000);
    
    if (diff < 60) return `${diff} seconds ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return lastUpdated.toLocaleString();
  };

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStudents = studentsHelpedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(studentsHelpedData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Chart Options
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: {
            size: 12,
            weight: '600'
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.label + ': PKR ' + context.parsed.toLocaleString();
          }
        }
      }
    }
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
        position: 'top'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return 'PKR ' + value.toLocaleString();
          }
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: {
            size: 12,
            weight: '600'
          }
        }
      }
    }
  };

  return (
    <Adminlayout>
    <div className="reports-page">
      <Container fluid>
        {/* Success Alert */}
        {showSuccessAlert && (
          <Alert variant="success" className="success-alert-email" dismissible onClose={() => setShowSuccessAlert(false)}>
            <CheckCircle size={20} className="me-2" />
            Email report sent successfully!
          </Alert>
        )}

        {/* Blue Header - Only Title and Subtitle */}
     <TitleBar
     title="Reports & Analytics"
     subtitle="Comprehensive overview of donations and students helpe"
></TitleBar>
        {/* White Section - Last Updated, Dropdown, and Buttons */}
        <div className="reports-controls-section">
          <div className="last-updated-info">
            <span className="last-updated-label">Last Updated:</span>
            <span className="last-updated-time">{formatLastUpdated()}</span>
            <Form.Check 
              type="switch"
              id="auto-refresh-switch"
              label="Auto-refresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="auto-refresh-toggle"
            />
          </div>

          <div className="header-actions-reports">
            <Form.Select 
              value={reportType} 
              onChange={(e) => setReportType(e.target.value)}
              className="report-type-select"
            >
              {reportTypes.map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </Form.Select>
            <Button 
              className="refresh-btn" 
              onClick={handleManualRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw size={18} className={isRefreshing ? 'spinning' : ''} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button className="email-report-btn" onClick={handleEmailReport}>
              <Mail size={18} />
              Email Report
            </Button>
            <Button className="download-pdf-btn" onClick={handleDownloadPDF}>
              <Download size={18} />
              Download PDF
            </Button>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <Row className="metrics-row mb-4">
          <Col lg={2} md={4} sm={6} className="mb-3">
            <Card className="metric-card">
              <Card.Body>
                <div className="metric-icon donations-metric">
                  <DollarSign size={24} />
                </div>
                <h3 className="metric-value">PKR {keyMetrics.totalDonations.toLocaleString()}</h3>
                <p className="metric-label">Total Donations</p>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={2} md={4} sm={6} className="mb-3">
            <Card className="metric-card">
              <Card.Body>
                <div className="metric-icon students-metric">
                  <Users size={24} />
                </div>
                <h3 className="metric-value">{keyMetrics.totalStudents}</h3>
                <p className="metric-label">Students Helped</p>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={2} md={4} sm={6} className="mb-3">
            <Card className="metric-card">
              <Card.Body>
                <div className="metric-icon requests-metric">
                  <FileText size={24} />
                </div>
                <h3 className="metric-value">{keyMetrics.requestsProcessed}</h3>
                <p className="metric-label">Requests Processed</p>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={2} md={4} sm={6} className="mb-3">
            <Card className="metric-card">
              <Card.Body>
                <div className="metric-icon success-metric">
                  <TrendingUp size={24} />
                </div>
                <h3 className="metric-value">{keyMetrics.successRate}%</h3>
                <p className="metric-label">Success Rate</p>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={2} md={4} sm={6} className="mb-3">
            <Card className="metric-card">
              <Card.Body>
                <div className="metric-icon average-metric">
                  <DollarSign size={24} />
                </div>
                <h3 className="metric-value">PKR {keyMetrics.averageDonation.toLocaleString()}</h3>
                <p className="metric-label">Avg. Donation</p>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={2} md={4} sm={6} className="mb-3">
            <Card className="metric-card">
              <Card.Body>
                <div className="metric-icon category-metric">
                  <Award size={24} />
                </div>
                <h3 className="metric-value">{keyMetrics.topCategory}</h3>
                <p className="metric-label">Top Category</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Charts Section */}
        <Row className="charts-section">
          <Col lg={6} className="mb-4">
            <Card className="chart-card-report">
              <Card.Body>
                <h4 className="chart-title-report">
                  📊 Donations by Category
                </h4>
                <div className="chart-wrapper">
                  <Pie data={donationsByCategoryData} options={pieChartOptions} />
                </div>
                <div className="chart-summary">
                  <div className="summary-item">
                    <span className="summary-label">Total:</span>
                    <span className="summary-value">PKR 850,000</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Highest:</span>
                    <span className="summary-value">Fees (53%)</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6} className="mb-4">
            <Card className="chart-card-report">
              <Card.Body>
                <h4 className="chart-title-report">
                  📈 Requests Status Distribution
                </h4>
                <div className="chart-wrapper">
                  <Doughnut data={requestsStatusData} options={doughnutChartOptions} />
                </div>
                <div className="chart-summary">
                  <div className="summary-item">
                    <span className="summary-label">Total Requests:</span>
                    <span className="summary-value">300</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Approval Rate:</span>
                    <span className="summary-value">82%</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6} className="mb-4">
            <Card className="chart-card-report">
              <Card.Body>
                <h4 className="chart-title-report">
                  📉 Monthly Donations Trend (2024)
                </h4>
                <div className="chart-wrapper-large">
                  <Line data={monthlyTrendData} options={lineChartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6} className="mb-4">
            <Card className="chart-card-report">
              <Card.Body>
                <h4 className="chart-title-report">
                  🎓 Students by Institute
                </h4>
                <div className="chart-wrapper-large">
                  <Bar data={studentsByInstituteData} options={barChartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Students Helped Summary Table */}
        <Row>
          <Col lg={12}>
            <Card className="table-card-report">
              <Card.Body>
                <h4 className="table-title-report">
                  👥 Students Helped Summary
                </h4>
                <div className="table-responsive">
                  <Table hover className="students-summary-table">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Institute</th>
                        <th>Total Received</th>
                        <th>Requests Approved</th>
                        <th>Last Request</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentStudents.map((student) => (
                        <tr key={student.id}>
                          <td className="student-name-report">{student.name}</td>
                          <td>{student.institute}</td>
                          <td className="amount-cell">PKR {student.totalReceived.toLocaleString()}</td>
                          <td className="text-center">
                            <Badge bg="primary">{student.requestsApproved}</Badge>
                          </td>
                          <td>{student.lastRequest}</td>
                          <td>
                            <Badge bg="success">Active</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                
                {/* Pagination Controls */}
                <div className="pagination-controls">
                  <Button 
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  
                  <div className="pagination-numbers">
                    {[...Array(totalPages)].map((_, index) => (
                      <Button
                        key={index + 1}
                        className={`pagination-number ${currentPage === index + 1 ? 'active' : ''}`}
                        onClick={() => handlePageChange(index + 1)}
                      >
                        {index + 1}
                      </Button>
                    ))}
                  </div>
                  
                  <Button 
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Email Report Modal */}
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="lg" centered>
        <Modal.Header closeButton className="modal-header-email">
          <Modal.Title>
            <Mail size={24} className="me-2" />
            Email Report
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="email-form-label">Recipient Email *</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email address"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="email-input-field"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="email-form-label">Subject</Form.Label>
              <Form.Control
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="email-input-field"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="email-form-label">Message (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Add a personal message..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                className="email-textarea-field"
              />
            </Form.Group>

            <Alert variant="info" className="email-info-alert">
              <FileText size={18} className="me-2" />
              The complete report with all charts and data will be attached as a PDF.
            </Alert>

            <div className="email-preview-section">
              <h6 className="preview-title">Report Contents:</h6>
              <ul className="preview-list">
                <li>✅ Key Metrics Dashboard</li>
                <li>✅ Donations by Category Chart</li>
                <li>✅ Requests Status Distribution</li>
                <li>✅ Monthly Donations Trend</li>
                <li>✅ Students by Institute</li>
                <li>✅ Students Helped Summary Table</li>
              </ul>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
            Cancel
          </Button>
          <Button className="send-email-btn" onClick={sendEmailReport}>
            <Mail size={18} />
            Send Email
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
    </Adminlayout>
  );
};

export default Reports;